import { ChangeDetectorRef, Component, ElementRef, ViewChild, NgZone } from '@angular/core';
import { StandardTable } from '../../../../shared/components/standard-table/standard-table/standard-table';
import { OrderService } from '../../../../core/services/order.service';
import { AlertService } from '../../../../shared/components/alert/alert';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../../core/services/translation.service';
import { SocketService } from '../../../../core/services/socket.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-order',
  imports: [CommonModule, StandardTable, FormsModule, TranslatePipe],
  templateUrl: './order.html',
  styleUrl: './order.css',
})
export class Order {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  columnList = [
    { name: 'column.invoiceNo', key: 'invoiceno', sortable: false },
    { name: 'column.name', key: 'payment.name', sortable: false },
    { name: 'column.address', key: 'payment.address', sortable: false },
    { name: 'column.phone', key: 'payment.phone', sortable: false },
    { name: 'column.status', key: 'status', sortable: false },
    { name: 'column.screenshot', key: 'icon', sortable: false },
    { name: 'column.ticketCount', key: 'ticketlen', sortable: false },
    { name: 'column.action', key: 'actions', sortable: false },
  ];

  tableDataFilters: any[] = [];
  totalItems = 0;
  isLoading = false;

  currentPage = 1;
  pageSize = 10;
  searchQuery = '';

  orders: any = [];

  isSaving = false;
  isModalOpen = false;
  editingId: string | null = null;

  newOrder = {
    status: '',
  };

  filterStatus = '';
  filterDateRange = '';

  constructor(
    private orderService: OrderService,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private translationService: TranslationService,
    private socketService: SocketService,
    private ngZone: NgZone,
  ) {}

  private sub!: Subscription;

  ngOnInit(): void {
    this.sub = this.socketService.onNewOrder().subscribe((order) => {
      const transformed = this.transformOrder(order);

      setTimeout(() => {
        this.tableDataFilters = [transformed, ...this.tableDataFilters];
        console.log('UPDATED ARRAY:', this.tableDataFilters);
        this.cdr.detectChanges();
      });
    });
    this.loadOrders();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  loadOrders() {
    this.isLoading = true;

    const payload: any = {
      page: this.currentPage.toString(),
      limit: this.pageSize.toString(),
      search: this.searchQuery,
    };

    if (this.filterStatus) {
      payload.status = this.filterStatus;
    }

    if (this.filterDateRange) {
      const { startdate, enddate } = this.calculateDateRange(this.filterDateRange);
      payload.startdate = startdate;
      payload.enddate = enddate;
    }

    this.orderService.getAllOrders(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300') {
          this.tableDataFilters = res.data.data.map((t: any) => this.transformOrder(t));
          this.totalItems = res.data.meta.totalItems || res.data.meta.total || 0;
        } else {
          this.tableDataFilters = [];
          this.totalItems = 0;
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch orders in admin table:', err);
        this.tableDataFilters = [];
        this.totalItems = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  search(value: string) {
    this.searchQuery = value;
    this.currentPage = 1;
    this.loadOrders();
  }

  onStatusChange(status: string) {
    this.filterStatus = status;
    this.currentPage = 1;
    this.loadOrders();
  }

  onDateRangeChange(range: string) {
    this.filterDateRange = range;
    this.currentPage = 1;
    this.loadOrders();
  }

  calculateDateRange(range: string): { startdate: string; enddate: string } {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    switch (range) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        start.setDate(now.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(now.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end.setHours(23, 59, 59, 999);
        break;
    }

    return {
      startdate: start.toISOString().split('T')[0],
      enddate: end.toISOString().split('T')[0],
    };
  }

  onSort(column: any) {
    console.log('Sort:', column);
  }

  onPageChange(page: any) {
    this.currentPage = page;
    this.loadOrders();
  }

  onClickRow(row: any) {
    console.log('Row clicked:', row);
  }

  onEdit(row: any) {
    this.editingId = row.id;
    this.isModalOpen = true;
    this.newOrder = {
      status: row.status,
    };
  }

  closeModal() {
    this.isModalOpen = false;
  }

  async submit() {
    this.isSaving = true;
    const request = this.orderService.updateOrder({ ...this.newOrder, id: this.editingId });
    request.subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res.returncode === '300') {
          this.alertService.success(this.translationService.translate('alert.updateSuccess'));
          this.closeModal();
          this.loadOrders();
        } else {
          this.alertService.error(
            res.message || this.translationService.translate('alert.updateError'),
          );
        }
      },
      error: (err) => {
        this.isSaving = false;
        console.error(`Error updating order:`, err);
        this.alertService.error(this.translationService.translate('alert.errorOccurred'));
      },
    });
  }
  private transformOrder(t: any) {
    return {
      ...t,
      icon: t?.payment?.screenshot ?? '',
      ticketlen: t?.payment?.ticket?.length ?? 0,
    };
  }
}
